import React, { useRef, useEffect, useState } from 'react';
import { Box, Typography, Divider, Card, CardContent } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import InstagramIcon from '@mui/icons-material/Instagram';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import img1 from './images/hubungi.jpg';
import { Helmet } from 'react-helmet-async';

function Contact() {
  const sections = useRef([]);
  const [visibleSections, setVisibleSections] = useState({});

  const images = [
    {
      src: img1,
      title: 'HUBUNGI KAMI',
    },
  ];

  useEffect(() => {
    const currentSections = sections.current;

    const observer = new IntersectionObserver(
      (entries) => {
        const updatedVisibleSections = { ...visibleSections };
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            updatedVisibleSections[entry.target.dataset.index] = true;
          }
        });
        setVisibleSections(updatedVisibleSections);
      },
      { threshold: 0.5 }
    );

    currentSections.forEach((section) => {
      if (section) {
        observer.observe(section);
      }
    });

    return () => {
      currentSections.forEach((section) => {
        if (section) {
          observer.unobserve(section);
        }
      });
    };
  }, [visibleSections]);

  return (
    <Box>
      <Helmet>
        <title>Kontak Dealer Resmi BYD BSD Serpong | Info & Lokasi</title>
        <meta
          name='description'
          content='Hubungi dealer resmi BYD BSD Serpong sekarang untuk informasi harga, promo, test drive, dan pemesanan mobil listrik BYD Sealion 7, Seagull, Denza D9, dan lainnya.'
        />
        <meta
          name='keywords'
          content='BYD BSD Serpong, Dealer BYD BSD, Kontak BYD Serpong, Lokasi BYD BSD, Test Drive BYD, Harga Mobil Listrik BYD, Promo BYD Serpong'
        />
        <meta name='author' content='BYD BSD Serpong Junius' />
        <meta name='geo.region' content='ID-BT' />
        <meta
          name='geo.placename'
          content='BSD Serpong, Tangerang Selatan, Indonesia'
        />
        <meta name='geo.position' content='-6.3046;106.6496' />
        <meta name='ICBM' content='-6.3046, 106.6496' />
        <meta
          property='og:title'
          content='Kontak Dealer Resmi BYD BSD Serpong | Info & Lokasi'
        />
        <meta
          property='og:description'
          content='Hubungi kami untuk informasi dan pemesanan mobil listrik BYD di BSD Serpong. Kami siap bantu Anda!'
        />
        <meta property='og:url' content='https://bydbsdserpong.com/contact' />
        <meta
          property='og:image'
          content='https://bydbsdserpong.com/static/media/hubungi-kami.png'
        />
        <meta property='og:type' content='website' />
        <script type='application/ld+json'>
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'LocalBusiness',
            name: 'BYD BSD Serpong',
            image: 'https://bydbsdserpong.com/static/media/hubungi-kami.png',
            '@id': 'https://bydbsdserpong.com/contact',
            url: 'https://bydbsdserpong.com',
            telephone: '+6281286864825',
            address: {
              '@type': 'PostalAddress',
              streetAddress:
                'BSD City Serpong Kavling De Park, Jl. BSD Raya Utama Lot 1',
              addressLocality: 'Lengkong Kulon, Pagedangan',
              addressRegion: 'Banten',
              postalCode: '15331',
              addressCountry: 'ID',
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: -6.3046,
              longitude: 106.6496,
            },
            openingHours: 'Mo-Su 09:00-17:00',
            sameAs: [
              'https://www.instagram.com/bydbsdserpong/',
              'https://api.whatsapp.com/send?phone=6281286864825',
            ],
          })}
        </script>
      </Helmet>
      <Typography
        variant='h1'
        component='h1'
        fontWeight='bold'
        sx={{
          textAlign: 'center',
          marginBottom: 4,
          fontSize: { xs: '2rem', sm: '2.5rem' },
        }}
      >
        Hubungi
      </Typography>
      {images.map((img, index) => (
        <Box
          key={index}
          ref={(el) => (sections.current[index] = el)}
          data-index={index}
          sx={{
            position: 'relative',
            height: '90vh',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <Box
            component='img'
            src={img.src}
            alt={`Image ${index + 1}`}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
            }}
          />

          {img.title && (
            <Box
              sx={{
                position: 'absolute',
                top: { xs: '10%', sm: '15%' },
                color: '#fff',
                textAlign: 'center',
                padding: 2,
                transform: visibleSections[index]
                  ? 'translateY(0)'
                  : 'translateY(100%)',
                opacity: visibleSections[index] ? 1 : 0,
                transition: 'all 0.8s ease-in-out',
                backdropFilter: 'blur(5px)',
              }}
            >
              <Typography
                variant='h4'
                fontWeight='bold'
                sx={{
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                }}
              >
                {img.title}
              </Typography>
            </Box>
          )}
        </Box>
      ))}

      <Box
        sx={{
          padding: { xs: '20px', sm: '40px' },
          backgroundColor: '#f9f9f9',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 4,
          alignItems: 'center',
        }}
      >
        <Card
          sx={{
            padding: 3,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            transition: 'transform 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: '0 6px 15px rgba(0,0,0,0.2)',
            },
          }}
        >
          <CardContent>
            <Typography variant='h5' fontWeight='bold' gutterBottom>
              Hubungi Kami
            </Typography>
            <Divider sx={{ marginY: 2 }} />

            <Box
              sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}
            >
              <WhatsAppIcon sx={{ color: '#25D366', marginRight: 2 }} />
              <Typography>
                <a
                  href='https://api.whatsapp.com/send?phone=6281286864825&text=Halo%20saya%20mau%20tau%20info%20BYD%20nya'
                  target='_blank'
                  rel='noopener noreferrer'
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  0812 8686 4825
                </a>
              </Typography>
            </Box>

            <Box
              sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}
            >
              <InstagramIcon sx={{ color: '#C13584', marginRight: 2 }} />
              <Typography>
                <a
                  href='https://www.instagram.com/bydbsdserpong/'
                  target='_blank'
                  rel='noopener noreferrer'
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  @bydbsdserpong
                </a>
              </Typography>
            </Box>

            <Box
              sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}
            >
              <BusinessIcon sx={{ color: '#757575', marginRight: 2 }} />
              <Typography>
                <a
                  href='https://www.google.com/maps/place/BYD+Arista+BSD+City/@-6.2816255,106.6342924,17z/data=!3m1!4b1!4m6!3m5!1s0x2e69fb861088715d:0xcd66cbac6d05d9d0!8m2!3d-6.2816308!4d106.6368673!16s%2Fg%2F11svrps209?entry=ttu&g_ep=EgoyMDI1MDEwNy4wIKXMDSoASAFQAw%3D%3D'
                  target='_blank'
                  rel='noopener noreferrer'
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  BYD Arista BSD City
                </a>
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LocationOnIcon sx={{ color: '#F44336', marginRight: 2 }} />
              <Typography>
                BSD City Serpong Kavling De Park, Jl. BSD Raya Utama Lot 1,
                Lengkong Kulon, Kec. Pagedangan, Kabupaten Tangerang, Banten
                15331
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default Contact;
